import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { CContainer, CSidebar, CSidebarBrand, CSidebarNav } from '@coreui/react'
import { CIcon } from '@coreui/icons-react'
import {
  cilSpeedometer,
  cilUser,
  cilBell,
  cilPeople,
  cilChart,
  cilFolder,
  cilBuilding,
  cilReload
} from '@coreui/icons'
import MembersHeader from '../components/MembersHeader'
import MembersFooter from '../components/MembersFooter'
import ProfileCompletionGuard from '../components/ProfileCompletionGuard'
import StatusBanner from '../components/StatusBanner'
import useAutoLogout from '../hooks/useAutoLogout'

// Import CoreUI CSS only for the members portal
import '@coreui/coreui/dist/css/coreui.min.css'
import '../styles/members-portal.css'

const MembersLayout = ({ children }) => {
  // Auto-logout hook - logs out user after 30 minutes of inactivity
  useAutoLogout(30 * 60 * 1000) // 30 minutes in milliseconds

  // Get current location to determine active menu item
  const location = useLocation()

  const navigationSections = [
    {
      title: 'MAIN',
      items: [
        {
          name: 'Dashboard',
          to: '/members/dashboard',
          icon: cilSpeedometer,
        },
      ]
    },
    {
      title: 'CONTENT',
      items: [
        {
          name: 'Company Profile',
          to: '/members/company-profile',
          icon: cilBuilding,
        },
        {
          name: 'Documents',
          to: '/members/documents',
          icon: cilFolder,
        },
        {
          name: 'My Profile',
          to: '/members/my-profile',
          icon: cilUser,
        },
      ]
    },
    {
      title: 'SYSTEM',
      items: [
        {
          name: 'Notifications',
          to: '/members/notifications',
          icon: cilBell,
        }
      ]
    }
  ]

  // Function to check if a navigation item is active
  const isActiveNavItem = (itemPath) => {
    if (itemPath === '/members/dashboard') {
      return location.pathname === '/members/dashboard' || location.pathname === '/members' || location.pathname === '/members/'
    }
    if (itemPath === '/members/company-profile') {
      return location.pathname === '/members/company-profile' || location.pathname === '/members/profile'
    }
    return location.pathname === itemPath
  }

  return (
    <div className="members-portal">
      <div className="d-flex">
        <CSidebar
          position="fixed"
          unfoldable={false}
          visible={true}
          className="d-print-none modern-sidebar sidebar-fixed"
        >
          <CSidebarBrand className="modern-sidebar-brand px-4 py-4" to="/members/dashboard">
            <div className="sidebar-brand-full d-flex align-items-center justify-content-center">
              <div className="brand-container">
                <img
                  src="/logo.jpg"
                  alt="KPCA Logo"
                  className="brand-logo"
                />
                <div className="brand-text">
                  <span className="brand-title">KPCA</span>
                  <span className="brand-subtitle">Members Portal</span>
                </div>
              </div>
            </div>
          </CSidebarBrand>

          <CSidebarNav className="modern-sidebar-nav">
            <div className="nav-content">
              {navigationSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="nav-section">
                  <div className="nav-section-header">
                    {section.title}
                  </div>
                  <div className="nav-section-items">
                    {section.items.map((item, itemIndex) => {
                      const isActive = isActiveNavItem(item.to)
                      return (
                        <Link
                          key={itemIndex}
                          to={item.to}
                          className={`nav-item-link ${isActive ? 'active' : ''}`}
                        >
                          <div className="nav-item-content">
                            <div className="nav-item-icon">
                              <CIcon icon={item.icon} />
                            </div>
                            <span className="nav-item-text">{item.name}</span>
                            {isActive && <div className="nav-item-arrow">›</div>}
                          </div>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              ))}

              {/* Status Banner - positioned after SYSTEM section */}
              <div className="nav-section">
                <StatusBanner />
              </div>

            </div>
          </CSidebarNav>
        </CSidebar>

        <div className="wrapper d-flex flex-column min-vh-100 bg-light w-100">
          <MembersHeader />
          <div className="body flex-grow-1">
            <CContainer fluid className="h-auto px-0">
              <div className="px-4 py-4">
                {/* Profile Completion Guard - enforces profile completion before accessing other pages */}
                <ProfileCompletionGuard requiredThreshold={100}>
                  {children}
                </ProfileCompletionGuard>
              </div>
            </CContainer>
          </div>
          <MembersFooter />
        </div>
      </div>
    </div>
  )
}

export default MembersLayout 